import { useTheme } from '@shopify/restyle';

import {
  addDays,
  addMonths,
  getDay,
  getDaysInMonth,
  getDaysInMonthAsArray,
  isAfter,
  isBefore,
  isSameDay,
  isWithinInterval,
  roundToNextWholeWeek,
  setDate,
  startOfDay,
  startOfMonth,
  subDays,
  subMonths,
} from 'app/lib/date';
import type { ComponentProps, Dispatch, PropsWithChildren, SetStateAction } from 'react';
import { useEffect, useReducer, useRef } from 'react';
import { Column } from '../box';
import { Grid } from '../grid';
import { CALENDAR_DAY_SIZE, CalendarDay } from './day';
import { CalendarWeekIndicator } from './week-indicator';
import { StyleSheet, View, type LayoutRectangle } from 'react-native';
import { Gesture, GestureDetector } from 'react-native-gesture-handler';
import type { Theme } from 'app/shopify';

export interface CalendarMonthProps {
  current: Date;
  onDatePress?: (date: Date) => void;
  selected?: Date | Date[];
  disabled?: boolean;
  isUnavailableDay?: (date: Date) => boolean;
  startFromNow?: boolean;
  unavailableDates?: Date[];
  unavailableDateRanges?: Date[][];
  selectableUnavailableDates?: Date[];
  selectableUnavailableDateRanges?: Date[][];
  availableDates?: Date[]; // Dates to visually identify as available without selecting them
  userCurrentLoanDates?: Date[]; // Dates that are part of the user's current loan - these should always be selectable
  canSelectUnavailable?: boolean;
  daysSelectionMode?: 'interval' | 'individual'; // Select either an interval or individual days
  disableParentScroll?: Dispatch<SetStateAction<boolean>>;
  willNowChoose?: 'start' | 'end'; // Visual hint to show whether the user is choosing the start or end of an interval
  minCurrent?: Date; // Days before the start of this month are disabled
  maxCurrent?: Date; // Days after the end of this month are disabled
  minDate?: Date; // Days before this date are disabled
  maxDate?: Date; // Days after this date are disabled
}

interface CalendarPanCoordinates {
  isFinished: boolean;
  x: number;
  y: number;
}

interface CalendarPanGestureHandlers {
  onFinalize: (success: boolean) => void;
  onMove: (coordinates: CalendarPanCoordinates) => void;
  onStart: () => void;
}

const calendarPanGestureHandlers = new WeakMap<object, CalendarPanGestureHandlers>();

function createCalendarPanGesture(key: object, enabled: boolean) {
  return Gesture.Pan()
    .enabled(enabled)
    .minDistance(0)
    .runOnJS(true)
    .onStart(() => calendarPanGestureHandlers.get(key)?.onStart())
    .onUpdate((event) =>
      calendarPanGestureHandlers.get(key)?.onMove({
        isFinished: false,
        x: event.absoluteX,
        y: event.absoluteY,
      }),
    )
    .onEnd((event) =>
      calendarPanGestureHandlers.get(key)?.onMove({
        isFinished: true,
        x: event.absoluteX - event.translationX,
        y: event.absoluteY - event.translationY,
      }),
    )
    .onFinalize((_event, success) => calendarPanGestureHandlers.get(key)?.onFinalize(success));
}

function useCalendarBoundaries(
  useBoundaries: boolean,
  start: Date | null,
  end: Date | null,
  current: Date,
  unavailableDates?: Date[],
  canSelectUnavailable?: boolean,
  pickInterval?: boolean,
): { getBoundaries: (date: Date) => { isTooEarly: boolean | undefined; isTooLate: boolean | undefined } } {
  // When a start date is selected, disable all dates starting at the next & previous unavailable dates.
  // This will prevent the user from being able to select an unavailable date by selecting before and after it.
  const sortedUnavailableDatesCopy = (reversed: boolean) =>
    [...(unavailableDates ?? [])].sort((a, b) => (isBefore(reversed ? b : a, reversed ? a : b) ? -1 : 1));

  const min = (() => {
    if (!useBoundaries || canSelectUnavailable || !pickInterval) return undefined;
    const previousUnavailable = sortedUnavailableDatesCopy(true).find((date) => isBefore(date, start ?? current));
    return previousUnavailable ? addDays(previousUnavailable, 1) : undefined;
  })();

  const max = (() => {
    if (!useBoundaries || !start || canSelectUnavailable || !pickInterval) return undefined;
    const nextUnavailable = sortedUnavailableDatesCopy(false).find((date) => isAfter(date, end ?? start));
    return nextUnavailable ? subDays(nextUnavailable, 1) : undefined;
  })();

  const getBoundaries = (date: Date) => {
    if (!useBoundaries) return { isTooEarly: false, isTooLate: false };
    const isTooLate = max && isAfter(date, max);
    const isTooEarly = min && isBefore(date, min) && !isSameDay(date, min);
    return { isTooEarly, isTooLate };
  };

  return { getBoundaries };
}

function CalendarMonthGrid({
  availableDates,
  canSelectUnavailable,
  current,
  cutoffDate,
  disabled,
  end,
  getBoundaries,
  isUnavailableDay,
  maxCurrent,
  maxDate,
  minCurrent,
  minDate,
  onDatePress,
  registerTouchableComponent,
  selectableUnavailableDateRanges,
  selectableUnavailableDates,
  selected,
  start,
  unavailableDateRanges,
  unavailableDates,
  userCurrentLoanDates,
  willNowChoose,
}: Pick<
  CalendarMonthProps,
  | 'availableDates'
  | 'canSelectUnavailable'
  | 'current'
  | 'disabled'
  | 'isUnavailableDay'
  | 'maxCurrent'
  | 'maxDate'
  | 'minCurrent'
  | 'minDate'
  | 'onDatePress'
  | 'selectableUnavailableDateRanges'
  | 'selectableUnavailableDates'
  | 'selected'
  | 'unavailableDateRanges'
  | 'unavailableDates'
  | 'userCurrentLoanDates'
  | 'willNowChoose'
> & {
  cutoffDate: Date | null;
  end: Date | null;
  getBoundaries: ReturnType<typeof useCalendarBoundaries>['getBoundaries'];
  registerTouchableComponent: (date: Date, view: View | null) => void;
  start: Date | null;
}) {
  const theme = useTheme<Theme>();
  const calendarCellHeight = theme.spacing['spacing.general.x10'];
  const minCurrentMonth = minCurrent ? startOfMonth(minCurrent) : null;
  const maxCurrentMonth = maxCurrent ? startOfMonth(maxCurrent) : null;
  const minSelectableDate = minDate ? startOfDay(minDate) : null;
  const maxSelectableDate = maxDate ? startOfDay(maxDate) : null;
  const isRange = Boolean(start && end);
  const displayableDates = getDisplayableDates(current);
  const getHighlightKind = (date: Date) => {
    if (cutoffDate && isBefore(date, cutoffDate)) return undefined;
    const isWithinSelectedRange = Boolean(isRange && start && end && isWithinInterval(date, { start, end }));
    const isPartOfUserCurrentLoan = userCurrentLoanDates?.some((loanDate) => isSameDay(loanDate, date));
    const isUnavailable = isUnavailableDay
      ? isUnavailableDay(date)
      : !isPartOfUserCurrentLoan && unavailableDates?.some((unavailableDate) => isSameDay(unavailableDate, date));
    const isSelectableUnavailable = selectableUnavailableDates?.some((unavailableDate) =>
      isSameDay(unavailableDate, date),
    );
    const isWithinUnavailableRange = unavailableDateRanges?.some(
      (range) => range.length > 1 && range.some((rangeDate) => isSameDay(rangeDate, date)),
    );
    const isWithinSelectableUnavailableRange = selectableUnavailableDateRanges?.some(
      (range) => range.length > 1 && range.some((rangeDate) => isSameDay(rangeDate, date)),
    );
    if (isUnavailable && isWithinUnavailableRange) return 'unavailable';
    if (isSelectableUnavailable && isWithinSelectableUnavailableRange) return 'selectableUnavailable';
    if (isWithinSelectedRange) return 'selected';
    return undefined;
  };

  return (
    <Column gap="spacing.general.x3">
      <CalendarWeekIndicator />

      <Grid columnGap="spacing.general.x2" rowGap="spacing.general.0" columns={7}>
        {displayableDates.map((date, index) => {
          const isEnd = isRange && end && isSameDay(date, end);
          const isStart = isRange && start && isSameDay(date, start);
          const { isTooEarly, isTooLate } = getBoundaries(date);
          const isWithinRange = isRange && start && end && isWithinInterval(date, { start, end });
          const isBeforeCutoff = Boolean(cutoffDate && isBefore(date, cutoffDate));
          const isBeforeMinimum = minSelectableDate ? isBefore(date, minSelectableDate) : false;
          const isAfterMaximum = maxSelectableDate ? isAfter(date, maxSelectableDate) : false;
          const isBeforeMinMonth = minCurrentMonth ? isBefore(startOfMonth(date), minCurrentMonth) : false;
          const isAfterMaxMonth = maxCurrentMonth ? isAfter(startOfMonth(date), maxCurrentMonth) : false;
          const isPartOfUserCurrentLoan = userCurrentLoanDates?.some((userCurrentLoanDate) =>
            isSameDay(userCurrentLoanDate, date),
          );
          const isUnavailable = isUnavailableDay
            ? isUnavailableDay(date)
            : !isPartOfUserCurrentLoan && unavailableDates?.some((unavailableDate) => isSameDay(unavailableDate, date));
          const isSelectableUnavailable = selectableUnavailableDates?.some((unavailableDate) =>
            isSameDay(unavailableDate, date),
          );
          const isAvailable = availableDates?.some((availableDate) => isSameDay(availableDate, date));
          const unavailableDateRange = getDateRangeContaining(date, unavailableDateRanges);
          const selectableUnavailableDateRange = getDateRangeContaining(date, selectableUnavailableDateRanges);
          const specialDateRange = isBeforeCutoff
            ? undefined
            : isUnavailable
              ? unavailableDateRange
              : isSelectableUnavailable
                ? selectableUnavailableDateRange
                : undefined;
          const isWithinSpecialRange = Boolean(specialDateRange && specialDateRange.length > 1);
          const isHighlighted = !isBeforeCutoff && (isWithinRange || isWithinSpecialRange);
          const highlightKind = getHighlightKind(date);
          const previousDate = index % 7 !== 0 ? displayableDates[index - 1] : undefined;
          const nextDate = (index + 1) % 7 !== 0 ? displayableDates[index + 1] : undefined;
          const isOutOfMonth = date.getMonth() !== current.getMonth();
          const hasPreviousHighlightedDate = previousDate ? getHighlightKind(previousDate) === highlightKind : false;
          const hasNextHighlightedDate = nextDate ? getHighlightKind(nextDate) === highlightKind : false;
          const isContinuousRangeStart = isHighlighted && !hasPreviousHighlightedDate;
          const isContinuousRangeEnd = isHighlighted && !hasNextHighlightedDate;
          const isStandaloneHighlight = isContinuousRangeStart && isContinuousRangeEnd;
          const isSelected = Array.isArray(selected)
            ? selected.some((value) => isSameDay(value, date))
            : selected
              ? isSameDay(selected, date)
              : false;
          const highlightBackgroundColor =
            isSelectableUnavailable && isWithinRange
              ? theme.colors['surface.brand.subtle']
              : isUnavailable
                ? theme.colors['surface.brand.secondary-4.subtle']
                : isSelectableUnavailable && !isWithinRange
                  ? theme.colors['surface.warning.subtle']
                  : disabled
                    ? theme.colors['surface.inverse.tertiary-neutral']
                    : theme.colors['surface.brand.subtle'];

          const column: ComponentProps<typeof Column> = {
            alignItems: 'center',
            flex: 1,
            height: calendarCellHeight,
          };

          return (
            <Column key={date.getTime().toString()} {...column}>
              {isHighlighted && !isStandaloneHighlight ? (
                <>
                  <View
                    pointerEvents="none"
                    style={[
                      styles.rangeHighlight,
                      {
                        backgroundColor: highlightBackgroundColor,
                        height: CALENDAR_DAY_SIZE,
                        top: (calendarCellHeight - CALENDAR_DAY_SIZE) / 2,
                      },
                      hasPreviousHighlightedDate && { left: -theme.spacing['spacing.general.x1'] },
                      hasNextHighlightedDate && { right: -theme.spacing['spacing.general.x1'] },
                      isContinuousRangeStart && styles.rangeHighlightStart,
                      isContinuousRangeEnd && styles.rangeHighlightEnd,
                    ]}
                  />
                  {isContinuousRangeStart || isContinuousRangeEnd ? (
                    <View
                      pointerEvents="none"
                      style={[
                        styles.rangeHighlightCap,
                        {
                          backgroundColor: highlightBackgroundColor,
                          top: (calendarCellHeight - CALENDAR_DAY_SIZE) / 2,
                        },
                      ]}
                    />
                  ) : null}
                </>
              ) : null}
              <TouchableComponent date={date} registerComponent={registerTouchableComponent}>
                <CalendarDay
                  testID={formatCalendarDayTestID(date)}

                  disabled={
                    isTooEarly ||
                    isBeforeCutoff ||
                    isTooLate ||
                    isBeforeMinimum ||
                    isAfterMaximum ||
                    isBeforeMinMonth ||
                    isAfterMaxMonth ||
                    disabled ||
                    (!canSelectUnavailable && isUnavailable)
                  }
                  onPress={() => onDatePress?.(date)}
                  selected={isBeforeCutoff ? false : isSelected}
                  willNowChoose={willNowChoose}
                  variant={{
                    availability:
                      isUnavailable && !isBeforeCutoff
                        ? 'unavailable'
                        : isSelectableUnavailable && !isBeforeCutoff
                          ? 'selectable-unavailable'
                          : isPartOfUserCurrentLoan && !isBeforeCutoff
                            ? 'current-loan'
                            : isAvailable && !isBeforeCutoff
                              ? 'highlighted-available'
                              : 'available',
                    month: isOutOfMonth ? 'adjacent' : 'current',
                    rangePosition: isStart && isEnd ? 'start-and-end' : isStart ? 'start' : isEnd ? 'end' : 'none',
                  }}
                >
                  {date.getDate()}
                </CalendarDay>
              </TouchableComponent>
            </Column>
          );
        })}
      </Grid>
    </Column>
  );
}

export function CalendarMonth({
  current,
  onDatePress,
  selected,
  disabled,
  isUnavailableDay,
  unavailableDates,
  unavailableDateRanges,
  selectableUnavailableDates,
  selectableUnavailableDateRanges,
  availableDates,
  userCurrentLoanDates,
  canSelectUnavailable,
  daysSelectionMode = 'interval',
  disableParentScroll,
  willNowChoose,
  minCurrent,
  maxCurrent,
  minDate,
  maxDate,
  startFromNow,
}: CalendarMonthProps) {
  const minSelectableDate = minDate ? startOfDay(minDate) : null;
  const maxSelectableDate = maxDate ? startOfDay(maxDate) : null;
  // Ref for the calendar container (ancestor for measureLayout)
  const calendarContainerRef = useRef<View>(null);
  const containerPosition = useRef({ x: 0, y: 0 });

  const pickInterval = daysSelectionMode === 'interval';

  const end = pickInterval ? getEnd(selected) : null;
  const start = pickInterval ? getStart(selected) : null;
  const [today] = useReducer(
    (storedToday: Date) => storedToday,
    undefined,
    () => startOfDay(new Date()),
  );
  const cutoffDate = startFromNow ? today : null;

  // Locate (using measure()) all the touchable components (days) on the screen
  const touchableComponentRefs = useRef<{ date: Date; layout?: LayoutRectangle }[]>([]);
  const registerTouchableComponent = (date: Date, view: View | null) => {
    if (!view || !calendarContainerRef.current) return;
    view.measureLayout(calendarContainerRef.current, (x, y, width, height) => {
      const layout = { x, y, width, height };
      touchableComponentRefs.current = [
        ...touchableComponentRefs.current.filter((ref) => ref.date !== date),
        {
          date,
          layout,
        },
      ];
    });
  };

  const previouslyOver = useRef<Date>(null);
  const isMoveSelecting = useRef(false);

  // Throttle the onMoveHandler to fire at most once every 50ms
  const moveHandlerTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const lastMoveHandlerArgs = useRef<CalendarPanCoordinates | null>(null);

  // This will allow tracking the user's touch on the calendar, if daysSelectionMode === 'individual'.
  // Gesture Handler retains this object outside React. Keep the gesture stable while
  // refreshing its event implementation after each commit.
  const [panGestureKey] = useReducer(
    (stored: object) => stored,
    undefined,
    () => ({}),
  );
  useEffect(() => {
    const processMoveHandler = ({ isFinished, x, y }: CalendarPanCoordinates) => {
      if (isMoveSelecting.current && isFinished) {
        // Moved around = nothing else to do
        isMoveSelecting.current = false;
        disableParentScroll?.(false);
        return;
      }

      if (isFinished) {
        isMoveSelecting.current = false;
        disableParentScroll?.(false);
      }

      // Adjust the touch coordinates to be relative to the calendar container
      const moveX = x - containerPosition.current.x;
      const moveY = y - containerPosition.current.y;

      touchableComponentRefs.current.forEach(({ date, layout }: { date: Date; layout?: LayoutRectangle }) => {
        if (
          layout &&
          !(cutoffDate && isBefore(date, cutoffDate)) &&
          !(minSelectableDate && isBefore(date, minSelectableDate)) &&
          !(maxSelectableDate && isAfter(date, maxSelectableDate)) &&
          moveX >= layout.x &&
          moveX <= layout.x + layout.width &&
          moveY >= layout.y &&
          moveY <= layout.y + layout.height
        ) {
          if (date.getTime() !== previouslyOver.current?.getTime()) {
            isMoveSelecting.current = true;
            previouslyOver.current = date;
            onDatePress?.(date);
          }
        }
      });
    };

    const onMoveHandler = (coordinates: CalendarPanCoordinates) => {
      lastMoveHandlerArgs.current = coordinates;
      if (moveHandlerTimeout.current) return;
      moveHandlerTimeout.current = setTimeout(() => {
        moveHandlerTimeout.current = null;
        if (lastMoveHandlerArgs.current) {
          processMoveHandler(lastMoveHandlerArgs.current);
          lastMoveHandlerArgs.current = null;
        }
      }, 50);
    };

    const handlers: CalendarPanGestureHandlers = {
      onFinalize: (success) => {
        disableParentScroll?.(false);
        if (!success) {
          if (moveHandlerTimeout.current) {
            clearTimeout(moveHandlerTimeout.current);
            moveHandlerTimeout.current = null;
          }
          lastMoveHandlerArgs.current = null;
          isMoveSelecting.current = false;
        }
      },
      onMove: onMoveHandler,
      onStart: () => disableParentScroll?.(true),
    };
    calendarPanGestureHandlers.set(panGestureKey, handlers);
    return () => {
      if (moveHandlerTimeout.current) {
        clearTimeout(moveHandlerTimeout.current);
        moveHandlerTimeout.current = null;
      }
      if (calendarPanGestureHandlers.get(panGestureKey) === handlers) {
        calendarPanGestureHandlers.delete(panGestureKey);
      }
    };
  }, [cutoffDate, disableParentScroll, maxSelectableDate, minSelectableDate, onDatePress, panGestureKey]);

  const panGesture = createCalendarPanGesture(panGestureKey, daysSelectionMode === 'individual');

  const { getBoundaries } = useCalendarBoundaries(
    false,
    start,
    end,
    current,
    unavailableDates,
    canSelectUnavailable,
    pickInterval,
  );

  const calendarMonth = (
    <View
      ref={calendarContainerRef}
      onLayout={() => {
        setTimeout(() => {
          calendarContainerRef.current?.measure((_x, _y, _width, _height, pageX, pageY) => {
            containerPosition.current = { x: pageX, y: pageY };
          });
        }, 0);
      }}
    >
      <CalendarMonthGrid
        availableDates={availableDates}
        canSelectUnavailable={canSelectUnavailable}
        current={current}
        cutoffDate={cutoffDate}
        disabled={disabled}
        end={end}
        getBoundaries={getBoundaries}
        isUnavailableDay={isUnavailableDay}
        maxCurrent={maxCurrent}
        maxDate={maxDate}
        minCurrent={minCurrent}
        minDate={minDate}
        onDatePress={onDatePress}
        registerTouchableComponent={registerTouchableComponent}
        selectableUnavailableDateRanges={selectableUnavailableDateRanges}
        selectableUnavailableDates={selectableUnavailableDates}
        selected={selected}
        start={start}
        unavailableDateRanges={unavailableDateRanges}
        unavailableDates={unavailableDates}
        userCurrentLoanDates={userCurrentLoanDates}
        willNowChoose={willNowChoose}
      />
    </View>
  );

  return <GestureDetector gesture={panGesture}>{calendarMonth}</GestureDetector>;
}

const styles = StyleSheet.create({
  rangeHighlight: {
    ...StyleSheet.absoluteFill,
  },
  rangeHighlightStart: {
    left: '50%',
  },
  rangeHighlightEnd: {
    right: '50%',
  },
  rangeHighlightCap: {
    borderRadius: CALENDAR_DAY_SIZE / 2,
    height: CALENDAR_DAY_SIZE,
    left: '50%',
    position: 'absolute',
    transform: [{ translateX: -CALENDAR_DAY_SIZE / 2 }],
    width: CALENDAR_DAY_SIZE,
  },
});

// View that, onLayout, registers itself as a touchable component
function TouchableComponent({
  date,
  registerComponent,
  children,
}: PropsWithChildren<{ date: Date; registerComponent: (date: Date, view: View | null) => void }>) {
  const ref = useRef<View>(null);
  return (
    <View
      onLayout={() => registerComponent(date, ref.current)}
      ref={ref}
      style={{ flex: 1, alignItems: 'center', justifyContent: 'center' }}
    >
      {children}
    </View>
  );
}

function getStart(selected: CalendarMonthProps['selected']): Date | null {
  const start = Array.isArray(selected) ? selected[0] : selected;
  return start ?? null;
}

function getEnd(selected: CalendarMonthProps['selected']): Date | null {
  const start = getStart(selected);
  const last = Array.isArray(selected) ? selected[selected.length - 1] : selected;

  if (!start || !last) return null;

  return isSameDay(start, last) ? null : last;
}

function getDateRangeContaining(date: Date, ranges?: Date[][]): Date[] | undefined {
  return ranges?.find((range) => range.some((rangeDate) => isSameDay(rangeDate, date)));
}

function formatCalendarDayTestID(date: Date) {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `calendar-day-${String(year)}-${month}-${day}`;
}

function getDisplayableDates(current: Date) {
  const nextMonth = addMonths(current, 1);
  const previousMonth = subMonths(current, 1);
  const startDayOfCurrentMonth = getDay(startOfMonth(current));
  const numberOfDaysInPreviousMonth = getDaysInMonth(previousMonth);

  const currentMonthDaysArray = getDaysInMonthAsArray(current);
  const previousMonthDaysArray = Array.from({ length: startDayOfCurrentMonth }, (_, i) =>
    setDate(previousMonth, numberOfDaysInPreviousMonth - i),
  ).reverse();

  const nextMonthToDisplay = (() => {
    const numberDaysOfWithNextWholeWeek = roundToNextWholeWeek(
      previousMonthDaysArray.length + currentMonthDaysArray.length,
    );

    return numberDaysOfWithNextWholeWeek - previousMonthDaysArray.length - currentMonthDaysArray.length;
  })();

  return [
    ...previousMonthDaysArray,
    ...currentMonthDaysArray,
    ...Array.from({ length: nextMonthToDisplay }, (_, i) => setDate(nextMonth, i + 1)),
  ];
}
